import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@cloudscape-design/components/box';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import type { UsageHeatCell } from './useUsage';

const WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function colorFor(value: number, max: number): string {
  if (max === 0 || value === 0) return '#f1f5f9';
  const intensity = Math.min(1, value / max);
  if (intensity >= 0.8) return '#1e3a8a';
  if (intensity >= 0.6) return '#1d4ed8';
  if (intensity >= 0.4) return '#3b82f6';
  if (intensity >= 0.2) return '#60a5fa';
  return '#bfdbfe';
}

/**
 * Heatmap weekday × dia (dia da semana × volume). Sem equivalente nativo no
 * Cloudscape (Sprint 39) — células coloridas custom.
 */
export function UsageHeatmap({ cells }: { cells: readonly UsageHeatCell[] }): React.JSX.Element {
  const { t } = useTranslation();
  const totalsByWeekday = new Array<number>(7).fill(0);
  let max = 0;
  for (const c of cells) {
    totalsByWeekday[c.weekday] = (totalsByWeekday[c.weekday] ?? 0) + c.totalLabels;
  }
  for (const v of totalsByWeekday) {
    if (v > max) max = v;
  }

  return (
    <Container header={<Header variant="h3" description={t('usage.heatmap.hint')}>{t('usage.heatmap.title')}</Header>}>
      <ColumnLayout columns={7}>
        {WEEKDAYS.map((wd, i) => {
          const value = totalsByWeekday[i] ?? 0;
          return (
            <div key={wd} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <div
                title={`${t(`usage.heatmap.weekday.${wd}`)}: ${String(value)}`}
                style={{
                  display: 'flex',
                  height: 64,
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 12,
                  backgroundColor: colorFor(value, max),
                }}
              >
                {value > 0 ? value : ''}
              </div>
              <Box fontSize="body-s" color="text-status-inactive">
                {t(`usage.heatmap.weekday.${wd}`)}
              </Box>
            </div>
          );
        })}
      </ColumnLayout>
    </Container>
  );
}
